import React, { useEffect, useState } from 'react';
import { 
    Accordion, 
    AccordionDetails, 
    AccordionSummary, 
    Chip, 
    CircularProgress, 
    Typography, 
    Paper, 
    IconButton, 
    Alert, 
    AlertTitle,
    Box,
    Card,
    CardContent,
    Grid,
    Stack,
    Divider,
    useTheme,
    alpha,
    Fade,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Avatar,
    Skeleton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DeleteRatingData, getRatingData } from '../../Queries';
import RatingFormUsers from './RatingFormUsers';
import RatingForm from './RatingForm';
import RatingHistoryDialog from '../RatingHistoryDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CodeIcon from '@mui/icons-material/Code';
import WarningIcon from '@mui/icons-material/Warning';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PsychologyIcon from '@mui/icons-material/Psychology';
import WorkIcon from '@mui/icons-material/Work';
import HistoryIcon from '@mui/icons-material/History';

const RatingFormsTable = ({ refresh, handleRefresh }) => {
    const theme = useTheme();
    const [ratingData, setRatingData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [expandedProgramCode, setExpandedProgramCode] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [itemToPreview, setItemToPreview] = useState(null);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [selectedUserForHistory, setSelectedUserForHistory] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [itemToEdit, setItemToEdit] = useState(null);
    const [showRatingForm, setShowRatingForm] = useState(false);
    const [editKey, setEditKey] = useState(0);
    const [alert, setAlert] = useState({
        open: false,
        severity: 'success',
        message: '',
    });

    const getStatus = (endDate) => {
        const currentDate = new Date();
        const formattedEndDate = new Date(endDate);
        return currentDate <= formattedEndDate ? 'Active' : 'Closed';
    };

    const getStatusConfig = (status) => {
        return status === 'Active' 
            ? {
                color: 'success',
                bgcolor: alpha('#4CAF50', 0.1),
                textColor: '#2E7D32',
                icon: <CheckCircleIcon sx={{ fontSize: 16 }} />
              }
            : {
                color: 'error',
                bgcolor: alpha('#f44336', 0.1),
                textColor: '#C62828',
                icon: <CancelIcon sx={{ fontSize: 16 }} />
              };
    };

    const handleAccordionClick = (item) => {
        // Reset all dialog states when accordion is clicked
        setPreviewDialogOpen(false);
        setItemToPreview(null);
        setShowRatingForm(false);
        setItemToEdit(null);
        setEditDialogOpen(false);
        setDeleteDialogOpen(false);
        setItemToDelete(null);
        
        setExpandedProgramCode((prevCode) => (prevCode === item.id ? null : item.id));
    };

    const getRatingForms = async () => {
        try {
            const ratingForms = await getRatingData();
            setRatingData(ratingForms);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
        }
    };


    const handleSuccess = (message) => {
        handleRefresh();
        setAlert({
            open: true,
            severity: 'success',
            message,
        });
    };

    const handleError = (message) => {
        handleRefresh();
        setAlert({
            open: true,
            severity: 'error',
            message,
        });
    };

    const deleteRatingForms = async (id, email) => {
        try {
            const ratingForms = await DeleteRatingData(id, email);
            handleSuccess(ratingForms.message);
            setDeleteDialogOpen(false);
            setItemToDelete(null);
        } catch (error) {
            handleError("Unable to delete the form, Please try again.");
        }
    };

    const handleDeleteClick = (item, e) => {
        e.stopPropagation();
        // Reset all other states to ensure clean delete
        setPreviewDialogOpen(false);
        setItemToPreview(null);
        setShowRatingForm(false);
        setItemToEdit(null);
        setEditDialogOpen(false);
        
        // Set delete data and show delete dialog
        setItemToDelete(item);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = () => {
        if (itemToDelete) {
            deleteRatingForms(itemToDelete.form_id, itemToDelete.created_by);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
        setItemToDelete(null);
    };

    const handleEditClick = (item, e) => {
        e.stopPropagation();
        e.preventDefault();
        
        // Reset ALL states first
        setPreviewDialogOpen(false);
        setItemToPreview(null);
        setEditDialogOpen(false);
        setDeleteDialogOpen(false);
        setItemToDelete(null);
        
        // Then set edit states
        setTimeout(() => {
            setItemToEdit(item);
            setEditKey(prev => prev + 1);
            setShowRatingForm(true);
        }, 0);
    };

    const handleEditClose = (result) => {
        setShowRatingForm(false);
        setItemToEdit(null);
        
        if (result && result.message) {
            handleSuccess(result.message || 'Form updated successfully!');
        }
        
        // Always refresh the table data after edit
        getRatingForms();
    };

    const handlePreviewClick = (item, e) => {
        e.stopPropagation();
        e.preventDefault();
        
        // Reset ALL states first
        setShowRatingForm(false);
        setItemToEdit(null);
        setEditDialogOpen(false);
        setDeleteDialogOpen(false);
        setItemToDelete(null);
        
        // Then set preview states
        setTimeout(() => {
            setItemToPreview(item);
            setPreviewDialogOpen(true);
        }, 0);
    };

    const handlePreviewClose = () => {
        setPreviewDialogOpen(false);
        setItemToPreview(null);
    };

    const handleHistoryClick = (item, e) => {
        e.stopPropagation();
        e.preventDefault();
        
        // Create a user object from the item data
        const user = {
            email: item.created_by,
            id: item.created_by,
            name: item.created_by // This might need to be enhanced based on your user data structure
        };
        
        setSelectedUserForHistory(user);
        setHistoryDialogOpen(true);
    };

    const handleHistoryClose = () => {
        setHistoryDialogOpen(false);
        setSelectedUserForHistory(null);
    };

    const handleCloseAlert = () => {
        setAlert({
            ...alert,
            open: false,
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Invalid Date';
        
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) {
                return 'Invalid Date';
            }
            return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return 'Invalid Date';
        }
    };

    // Enhanced loading component
    const LoadingComponent = () => (
        <Box sx={{ p: 2 }}>
            <Stack spacing={1.5}>
                {[1, 2, 3].map((item) => (
                    <Card key={item} elevation={0} sx={{ border: `1px solid ${theme.palette.divider}` }}>
                        <CardContent>
                            <Grid container spacing={1.5} alignItems="center">
                                <Grid item xs={12} sm={6} md={2}>
                                    <Skeleton variant="text" width="80%" height={24} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <Skeleton variant="text" width="80%" height={24} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Skeleton variant="text" width="90%" height={24} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 2 }} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <Skeleton variant="text" width="70%" height={24} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={1}>
                                    <Skeleton variant="circular" width={32} height={32} />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                ))}
            </Stack>
        </Box>
    );

    // Enhanced header component
    const TableHeader = () => (
        <Card 
            elevation={0}
            sx={{ 
                mb: 1.5,
                background: `linear-gradient(135deg, ${alpha('#0a0068', 0.05)} 0%, ${alpha('#0c0082', 0.02)} 100%)`,
                border: `1px solid ${alpha('#0a0068', 0.1)}`
            }}
        >
            <CardContent sx={{ py: 1.5 }}>
                <Grid container spacing={1.5} alignItems="center">
                    <Grid item xs={12} sm={6} md={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CalendarTodayIcon sx={{ fontSize: 18, color: '#0a0068' }} />
                            <Typography variant="subtitle2" fontWeight="bold" color="#0a0068" sx={{ fontSize: '0.7rem' }}>
                                Start Date
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CalendarTodayIcon sx={{ fontSize: 18, color: '#0a0068' }} />
                            <Typography variant="subtitle2" fontWeight="bold" color="#0a0068" sx={{ fontSize: '0.7rem' }}>
                                End Date
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <AssignmentIcon sx={{ fontSize: 18, color: '#0a0068' }} />
                            <Typography variant="subtitle2" fontWeight="bold" color="#0a0068" sx={{ fontSize: '0.7rem' }}>
                                Self-Reflection Name
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CheckCircleIcon sx={{ fontSize: 18, color: '#0a0068' }} />
                            <Typography variant="subtitle2" fontWeight="bold" color="#0a0068" sx={{ fontSize: '0.7rem' }}>
                                Status
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <CodeIcon sx={{ fontSize: 18, color: '#0a0068' }} />
                            <Typography variant="subtitle2" fontWeight="bold" color="#0a0068" sx={{ fontSize: '0.7rem' }}>
                                Program Code
                            </Typography>
                        </Stack>
                    </Grid>
                    <Grid item xs={12} sm={6} md={1}>
                        <Typography variant="subtitle2" fontWeight="bold" color="#0a0068" textAlign="center" sx={{ fontSize: '0.7rem' }}>
                            Actions
                        </Typography>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    // Enhanced row component
    const EvaluationRow = ({ item, index }) => {
        const status = getStatus(item.end_date);
        const statusConfig = getStatusConfig(status);
        const isExpanded = expandedProgramCode === item.id;

        return (
            <Fade in={true} timeout={300} style={{ transitionDelay: `${index * 50}ms` }}>
                <Card 
                    elevation={0}
                    sx={{ 
                        mb: 1.5,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                            borderColor: alpha('#0a0068', 0.3),
                            boxShadow: `0 4px 12px ${alpha('#0a0068', 0.1)}`,
                        }
                    }}
                >
                    <Accordion 
                        expanded={isExpanded}
                        onChange={() => handleAccordionClick(item)}
                        elevation={0}
                        sx={{ 
                            '&:before': { display: 'none' },
                            '& .MuiAccordionSummary-root': {
                                minHeight: 'auto',
                                '&.Mui-expanded': {
                                    minHeight: 'auto',
                                }
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={
                                <Avatar 
                                    sx={{ 
                                        width: 32, 
                                        height: 32, 
                                        bgcolor: alpha('#0a0068', 0.1),
                                        transition: 'transform 0.2s ease-in-out',
                                        transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                                    }}
                                >
                                    <ExpandMoreIcon sx={{ color: '#0a0068', fontSize: 20 }} />
                                </Avatar>
                            }
                            sx={{ 
                                px: 2,
                                py: 1.5,
                                '& .MuiAccordionSummary-content': {
                                    margin: 0,
                                    '&.Mui-expanded': {
                                        margin: 0,
                                    }
                                }
                            }}
                        >
                            <Grid container spacing={1.5} alignItems="center">
                                <Grid item xs={12} sm={6} md={2}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <CalendarTodayIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                                        <Typography variant="body2" fontWeight="500" sx={{ fontSize: '0.75rem' }}>
                                            {formatDate(item.start_date)}
                                        </Typography>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <CalendarTodayIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                                        <Typography variant="body2" fontWeight="500" sx={{ fontSize: '0.75rem' }}>
                                            {formatDate(item.end_date)}
                                        </Typography>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <PersonIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                                        <Typography variant="body2" fontWeight="600" color="primary" sx={{ fontSize: '0.75rem' }}>
                                            {item.name}
                                        </Typography>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <Chip
                                        icon={statusConfig.icon}
                                        label={status}
                                        size="small"
                                        sx={{
                                            bgcolor: statusConfig.bgcolor,
                                            color: statusConfig.textColor,
                                            fontWeight: 600,
                                            border: `1px solid ${alpha(statusConfig.textColor, 0.3)}`,
                                            '& .MuiChip-icon': {
                                                color: statusConfig.textColor
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <CodeIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
                                        <Typography variant="body2" fontWeight="500" sx={{ fontSize: '0.75rem' }}>
                                            {item.program_code}
                                        </Typography>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} sm={6} md={1}>
                                    <Box display="flex" justifyContent="center" gap={0.5}>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handlePreviewClick(item, e)}
                                            sx={{
                                                color: theme.palette.primary.main,
                                                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                                                borderRadius: '8px',
                                                mr: 0.5,
                                                '&:hover': {
                                                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                                                    transform: 'scale(1.1)',
                                                    boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
                                                },
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            <VisibilityIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleHistoryClick(item, e)}
                                            sx={{
                                                color: theme.palette.info.main,
                                                backgroundColor: alpha(theme.palette.info.main, 0.1),
                                                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                                                borderRadius: '8px',
                                                mr: 0.5,
                                                '&:hover': {
                                                    bgcolor: alpha(theme.palette.info.main, 0.2),
                                                    transform: 'scale(1.1)',
                                                    boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
                                                },
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            <HistoryIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleEditClick(item, e)}
                                            sx={{
                                                color: theme.palette.warning.main,
                                                backgroundColor: alpha(theme.palette.warning.main, 0.1),
                                                border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                                                borderRadius: '8px',
                                                mr: 0.5,
                                                '&:hover': {
                                                    bgcolor: alpha(theme.palette.warning.main, 0.2),
                                                    transform: 'scale(1.1)',
                                                    boxShadow: '0 2px 8px rgba(255, 152, 0, 0.3)',
                                                },
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={(e) => handleDeleteClick(item, e)}
                                            sx={{
                                                color: theme.palette.error.main,
                                                backgroundColor: alpha(theme.palette.error.main, 0.1),
                                                border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
                                                borderRadius: '8px',
                                                '&:hover': {
                                                    bgcolor: alpha(theme.palette.error.main, 0.2),
                                                    transform: 'scale(1.1)',
                                                    boxShadow: '0 2px 8px rgba(244, 67, 54, 0.3)',
                                                },
                                                transition: 'all 0.2s ease',
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Grid>
                            </Grid>
                        </AccordionSummary>
                        <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                            <Divider sx={{ mb: 2 }} />
                            <Paper 
                                elevation={0}
                                sx={{ 
                                    p: 2,
                                    backgroundColor: alpha('#f8f9fa', 0.5),
                                    border: `1px solid ${alpha('#0a0068', 0.1)}`,
                                    borderRadius: 2
                                }}
                            >
                                <RatingFormUsers 
                                    questions={JSON.parse(item.questions)} 
                                    status={status} 
                                    form={item} 
                                    refreshForms={getRatingForms} 
                                />
                            </Paper>
                        </AccordionDetails>
                    </Accordion>
                </Card>
            </Fade>
        );
    };

    // Enhanced delete confirmation dialog
    const DeleteConfirmDialog = () => (
        <Dialog 
            open={deleteDialogOpen} 
            onClose={handleDeleteCancel}
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    minWidth: 400
                }
            }}
        >
            <DialogTitle sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2,
                color: theme.palette.error.main
            }}>
                <Avatar sx={{ bgcolor: alpha(theme.palette.error.main, 0.1) }}>
                    <WarningIcon sx={{ color: theme.palette.error.main }} />
                </Avatar>
                <Box>
                    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '0.9rem' }}>
                        Confirm Deletion
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        This action cannot be undone
                    </Typography>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" sx={{ fontSize: '0.8rem' }}>
                    Are you sure you want to delete the evaluation "<strong>{itemToDelete?.name}</strong>"?
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontSize: '0.75rem' }}>
                    All associated data will be permanently removed.
                </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1 }}>
                <Button 
                    onClick={handleDeleteCancel}
                    variant="outlined"
                    sx={{ borderRadius: 2 }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleDeleteConfirm}
                    variant="contained"
                    color="error"
                    sx={{ borderRadius: 2 }}
                >
                    Delete
                </Button>
            </DialogActions>
        </Dialog>
    );

    // Preview Section component for the dialog
    const PreviewSection = ({ title, icon, data, color = "primary", isBasicInfo = false }) => {
        return (
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 2, 
                    mb: 1.5, 
                    backgroundColor: alpha(theme.palette[color].main, 0.02),
                    border: `1px solid ${alpha(theme.palette[color].main, 0.2)}`,
                    borderRadius: 2
                }}
            >
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                    {icon}
                    <Typography variant="h6" color={color} fontWeight="600" sx={{ fontSize: '0.9rem' }}>
                        {title}
                    </Typography>
                </Stack>
                <Box sx={{ pl: 4 }}>
                    {isBasicInfo ? (
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ 
                                color: theme.palette.primary.main, 
                                fontWeight: 600,
                                fontSize: '1rem',
                                mb: 1
                            }}>
                                {itemToPreview.name}
                            </Typography>
                            
                            <Typography variant="body2" sx={{ 
                                color: theme.palette.text.secondary,
                                fontSize: '0.75rem',
                                mb: 1
                            }}>
                                {itemToPreview.program_code || 'No program code'}
                            </Typography>
                            
                            <Typography variant="body2" sx={{ 
                                color: theme.palette.text.secondary,
                                fontSize: '0.75rem',
                                fontStyle: 'italic'
                            }}>
                                Period: {formatDate(itemToPreview.start_date)} - {formatDate(itemToPreview.end_date)}
                            </Typography>
                        </Box>
                    ) : Array.isArray(data) && data.length > 0 ? (
                        <Stack spacing={1}>
                            {data.map((item, index) => (
                                <Box key={index} sx={{ 
                                    display: 'flex', 
                                    alignItems: 'flex-start', 
                                    gap: 1 
                                }}>
                                    <Typography variant="body2" sx={{ 
                                        color: theme.palette.text.secondary,
                                        fontSize: '0.75rem',
                                        fontWeight: 500,
                                        minWidth: '20px'
                                    }}>
                                        {index + 1}.
                                    </Typography>
                                    <Typography variant="body1" sx={{ 
                                        color: theme.palette.text.primary,
                                        fontSize: '0.8rem',
                                        lineHeight: 1.5
                                    }}>
                                        {item}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    ) : typeof data === 'string' && data ? (
                        <Typography variant="body1" color="text.primary" sx={{ fontSize: '0.75rem' }}>
                            {data}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic" sx={{ fontSize: '0.7rem' }}>
                            Not specified
                        </Typography>
                    )}
                </Box>
            </Paper>
        );
    };

    // Preview Dialog Component
    const PreviewDialog = () => {
        if (!itemToPreview) return null;

        const questions = JSON.parse(itemToPreview.questions);
        
        return (
            <Dialog 
                open={previewDialogOpen} 
                onClose={handlePreviewClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        maxHeight: '90vh',
                    }
                }}
            >
                <DialogTitle sx={{ 
                    background: `linear-gradient(135deg, #0a0068 0%, #0c0082 100%)`,
                    color: 'white',
                    position: 'relative',
                    py: 3
                }}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                        <VisibilityIcon sx={{ fontSize: 28 }} />
                        <Typography variant="h5" component="div" fontWeight="600" sx={{ fontSize: '1rem' }}>
                            Self-Reflection Preview
                        </Typography>
                    </Stack>
                    <IconButton 
                        onClick={handlePreviewClose} 
                        sx={{ 
                            position: 'absolute', 
                            top: 12, 
                            right: 12,
                            color: 'white',
                            '&:hover': {
                                backgroundColor: alpha('#ffffff', 0.1)
                            }
                        }}
                    >
                        <CancelIcon />
                    </IconButton>
                </DialogTitle>
                
                <DialogContent sx={{ p: 2 }}>
                    <Box sx={{ mb: 2 }} style={{marginTop: '14px'}}>
                        {/* Basic Information Preview */}
                        <Paper 
                            elevation={0} 
                            sx={{ 
                                p: 2, 
                                mb: 1.5, 
                                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                borderRadius: 2
                            }}
                        >
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h6" sx={{ 
                                    color: theme.palette.primary.main, 
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    mb: 1
                                }}>
                                    {itemToPreview.name}
                                </Typography>
                                
                                <Typography variant="body2" sx={{ 
                                    color: theme.palette.text.secondary,
                                    fontSize: '0.75rem',
                                    mb: 1
                                }}>
                                    {itemToPreview.program_code || 'No program code'}
                                </Typography>
                                
                                <Typography variant="body2" sx={{ 
                                    color: theme.palette.text.secondary,
                                    fontSize: '0.75rem',
                                    fontStyle: 'italic'
                                }}>
                                    Period: {formatDate(itemToPreview.start_date)} - {formatDate(itemToPreview.end_date)}
                                </Typography>
                            </Box>
                        </Paper>
                        
                        {/* Technical Skills Preview */}
                        <PreviewSection 
                            title="Code Quality & Engineering" 
                            icon={<CodeIcon color="secondary" />}
                            data={questions.code_quality_engineering || []}
                            color="secondary"
                        />

                        {/* Professional Skills Preview */}
                        {/* <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                            <Grid item xs={6}> */}
                                <PreviewSection 
                                    title="Professional Habits" 
                                    icon={<WorkIcon color="warning" />}
                                    data={questions.professional_habits || []}
                                    color="warning"
                                />
                            {/* </Grid>
                            <Grid item xs={6}> */}
                                <PreviewSection 
                                    title="Communication & Collaboration" 
                                    icon={<WorkIcon color="error" />}
                                    data={questions.communication_collaboration || []}
                                    color="error"
                                />
                            {/* </Grid>
                        </Grid> */}

                        {/* Growth & Development Preview */}
                        {/* <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                            <Grid item xs={6}> */}
                                <PreviewSection 
                                    title="Learning & Skill Growth" 
                                    icon={<PsychologyIcon color="primary" />}
                                    data={questions.learning_skill_growth || []}
                                    color="primary"
                                />
                            {/* </Grid>
                            <Grid item xs={6}> */}
                                <PreviewSection 
                                    title="Task Ownership & Delivery" 
                                    icon={<PsychologyIcon color="secondary" />}
                                    data={questions.task_ownership_delivery || []}
                                    color="secondary"
                                />
                            {/* </Grid>
                        </Grid> */}

                        <PreviewSection 
                            title="Problem-Solving & Thinking" 
                            icon={<PsychologyIcon color="info" />}
                            data={questions.problem_solving_thinking || []}
                            color="info"
                        />
                    </Box>
                </DialogContent>
                
                <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
                    <Button 
                        onClick={handlePreviewClose}
                        variant="contained"
                        size="large"
                        sx={{
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            px: 4,
                            py: 1.5,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #0a0068 0%, #0c0082 100%)',
                            boxShadow: '0 4px 15px rgba(10, 0, 104, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #0c0082 0%, #0e009e 100%)',
                                boxShadow: '0 6px 20px rgba(10, 0, 104, 0.4)',
                                transform: 'translateY(-1px)',
                            },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        Close Preview
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    // Edit using RatingForm component
    const EditDialog = () => {
        return (
            <RatingForm
                key={`edit-${itemToEdit.id}-${editKey}`}
                handleRefresh={handleRefresh}
                editMode={true}
                editData={itemToEdit}
                onEditComplete={handleEditClose}
            />
        );
    };

    useEffect(() => {
        setIsLoading(true);
        getRatingForms();
    }, []);

    // Debug effect to monitor state changes
    useEffect(() => {
        console.log('=== STATE CHANGE ===', {
            showRatingForm,
            previewDialogOpen,
            itemToEdit: itemToEdit?.id,
            itemToPreview: itemToPreview?.id,
            editDialogOpen,
            deleteDialogOpen
        });
    }, [showRatingForm, previewDialogOpen, itemToEdit, itemToPreview, editDialogOpen, deleteDialogOpen]);

    return (
        <Box>
            {/* Alert */}
            {alert.open && (
                <Alert 
                    severity={alert.severity} 
                    onClose={handleCloseAlert}
                    sx={{ 
                        mb: 2,
                        borderRadius: 2,
                        '& .MuiAlert-message': {
                            fontSize: '1rem'
                        }
                    }}
                >
                    <AlertTitle sx={{ fontWeight: 'bold' }}>
                        {alert.severity === 'success' ? 'Success!' : 'Error'}
                    </AlertTitle>
                    {alert.message}
                </Alert>
            )}

            {/* Loading State */}
            {isLoading ? (
                <LoadingComponent />
            ) : (
                <Box>
                    {/* Table Header */}
                    <TableHeader />
                    
                    {/* Data Rows */}
                    {ratingData.length > 0 ? (
                        <Stack spacing={0}>
                            {ratingData.map((item, index) => (
                                <EvaluationRow key={item.id} item={item} index={index} />
                            ))}
                        </Stack>
                    ) : (
                        <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
                            <CardContent sx={{ textAlign: 'center', py: 6 }}>
                                <AssignmentIcon sx={{ fontSize: 48, color: theme.palette.text.disabled, mb: 1.5 }} />
                                <Typography variant="h6" color="text.secondary" gutterBottom sx={{ fontSize: '0.9rem' }}>
                                    No Self-Reflections Found
                                </Typography>
                                <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
                                    Create your first self-reflection to get started.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            )}

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog />
            
            {/* Edit Dialog - Only render when needed */}
            {showRatingForm && itemToEdit && <EditDialog />}
            
            {/* Preview Dialog - Only render when needed */}
            {previewDialogOpen && itemToPreview && <PreviewDialog />}

            {/* History Dialog - Only render when needed */}
            {historyDialogOpen && selectedUserForHistory && (
                <RatingHistoryDialog
                    open={historyDialogOpen}
                    onClose={handleHistoryClose}
                    selectedUser={selectedUserForHistory}
                />
            )}
        </Box>
    );
};

export default RatingFormsTable;